import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./LoginFB.css"; // Import the CSS file for styling
import logo from "./images/logo1.png"; // Import the logo image
import { FcGoogle } from "react-icons/fc"; // Import Google icon
import { FaFacebook } from "react-icons/fa"; // Import Facebook icon
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
} from "firebase/auth";
import { getDatabase, ref, get, set, update } from "firebase/database";
import { app } from "./firebase/config"; // Import app từ config
import {
  FACEBOOK_MANAGEMENT_SCOPE,
  fetchGrantedFacebookScopes,
  buildConnectedFanpageRecord,
} from "./utils/facebookCommentPermissions";
import {
  handleAffiliateLink,
  trackAffiliateSignup,
} from "./utils/affiliateUtils";
import { trackEvent, trackCompleteRegistration } from "./utils/metaPixel";
import { captureFacebookAdParams, getStoredAdParams } from "./utils/adTracking";
import { getFirstTouch, clearFirstTouch } from "./utils/sourceTracking";
import { createFacebookLoginFallback } from "./utils/facebookLoginFallback";
import { buildFacebookConnectedChannelMap } from "./utils/socialChannels";

const database = getDatabase(app);
const REDIRECT_ALIASES = {
  "affiliate-dashboard": "/affiliate",
};

export const parseCheckAndSaveIsNewUser = (payload) => {
  const candidates = [payload?.data?.isNewUser, payload?.isNewUser];

  for (const candidate of candidates) {
    if (candidate === true) {
      return true;
    }

    if (candidate === false) {
      return false;
    }
  }

  return null;
};

const LoginFB = () => {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userID, setUserID] = useState(null);
  const [fbReady, setFbReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fbAdParams, setFbAdParams] = useState({});
  const [emailLogin, setEmailLogin] = useState({ email: "", password: "" });
  const [emailLoginError, setEmailLoginError] = useState("");
  const [emailLoginInfo, setEmailLoginInfo] = useState("");

  const resolveRedirectPath = () => {
    const queryParams = new URLSearchParams(location.search);
    const requestedRedirect =
      localStorage.getItem("postLoginRedirect") ||
      queryParams.get("redirect") ||
      localStorage.getItem("fb_redirect_after") ||
      "/auto-post";

    const normalizedRedirect =
      REDIRECT_ALIASES[requestedRedirect] ||
      (requestedRedirect.startsWith("/")
        ? requestedRedirect
        : `/${requestedRedirect}`);

    if (
      !normalizedRedirect ||
      normalizedRedirect.startsWith("//") ||
      normalizedRedirect.includes("://")
    ) {
      return "/auto-post";
    }

    return normalizedRedirect;
  };

  const redirectAfterLogin = () => {
    const nextPath = resolveRedirectPath();
    localStorage.removeItem("postLoginRedirect");
    localStorage.removeItem("fb_redirect_after");
    window.location.href = nextPath;
  };

  useEffect(() => {
    const checkFBSDK = () => {
      if (window.FB) {
        setFbReady(true);
        setIsLoading(false);
        checkLoginState();
      } else {
        setTimeout(checkFBSDK, 100);
      }
    };

    checkFBSDK();

    // Xử lý affiliate link khi component mount
    handleAffiliateLink();

    // Capture Facebook ad parameters
    handleCaptureAdParams();

    // Xử lý OAuth redirect callback: parse access_token từ URL hash
    const hash = window.location.hash;
    if (hash && hash.includes("access_token")) {
      console.log("🔵 [FB Login] Detected OAuth redirect with access_token in URL hash");
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get("access_token");
      const expiresIn = params.get("expires_in");

      if (accessToken) {
        setIsLoading(true);
        // Xóa hash khỏi URL
        window.history.replaceState(null, "", window.location.pathname);
        handleOAuthRedirectToken(accessToken, expiresIn);
      }
    }

    // Xóa cờ redirecting khi component mount để đảm bảo không bị trạng thái cũ ảnh hưởng
    if (location.pathname === "/login") {
      localStorage.removeItem("redirecting");
    }

    return () => {
      // Cleanup khi component unmount
      localStorage.removeItem("redirecting");
    };
  }, [location]);

  // Capture Facebook ad parameters from URL using our utility function
  const handleCaptureAdParams = () => {
    const adParams = captureFacebookAdParams();
    if (adParams) {
      setFbAdParams(adParams);
    }
  };

  // useEffect(() => {
  //   console.log("2222333333");
  //   console.log(isLoggedIn);
  //   console.log(localStorage.getItem("redirecting"));
  //   // Kiểm tra nếu đã đăng nhập nhưng chưa được chuyển hướng
  //   if (isLoggedIn && !localStorage.getItem("redirecting")) {
  //     // Đặt cờ redirecting để tránh chuyển hướng nhiều lần
  //     localStorage.setItem("redirecting", "true");
  //     // Sử dụng navigate thay vì window.location để tận dụng React Router
  //     navigate("/auto-post", { replace: true });
  //   }
  // }, [isLoggedIn, navigate]);

  const checkLoginState = () => {
    if (!fbReady) return;

    window.FB.getLoginStatus((response) => {
      if (response.status === "connected") {
        setIsLoggedIn(true);
        setUserID(response.authResponse.userID);
      } else {
        setIsLoggedIn(false);
        setUserID(null);
      }
    });
  };

  // Xử lý đăng nhập Google sử dụng Firebase Auth
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const auth = getAuth(app);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const token = await user.getIdToken();

      // Lưu thông tin user và token vào localStorage
      localStorage.setItem("userId", user.uid);
      localStorage.setItem("userAccessToken", token);
      localStorage.setItem("userEmail", user.email || "");
      localStorage.setItem("loginMethod", "google");
      const authMetadata = await getAuthMetadata(auth, user, ["google"]);

      // Gọi API check-and-save, tracking affiliate, ad conversion, redirect (giống Facebook)
      await checkAndSaveUser(user.uid, user.email, authMetadata);

      // Thiết lập trạng thái đăng nhập và chuyển hướng
      setIsLoggedIn(true);
      localStorage.setItem("redirecting", "true");
      redirectAfterLogin();
    } catch (error) {
      console.error("Lỗi đăng nhập Google:", error);

      // Xử lý các trường hợp lỗi cụ thể
      if (error.code === "auth/popup-closed-by-user") {
        alert(
          "Đăng nhập không thành công! Bạn đã đóng cửa sổ đăng nhập Google."
        );
      } else if (error.code === "auth/cancelled-popup-request") {
        alert("Yêu cầu đăng nhập đã bị hủy.");
      } else if (error.code === "auth/popup-blocked") {
        alert(
          "Trình duyệt đã chặn cửa sổ đăng nhập. Vui lòng kiểm tra cài đặt trình duyệt của bạn."
        );
      } else {
        alert("Đăng nhập Google không thành công! Vui lòng thử lại sau.");
      }

      setIsLoading(false);
    }
  };

  const getAuthMetadata = async (auth, user, extraProviders = []) => {
    const providerSet = new Set(
      (user.providerData || [])
        .map((providerInfo) => providerInfo?.providerId)
        .filter(Boolean)
    );
    extraProviders.filter(Boolean).forEach((providerId) => providerSet.add(providerId));

    const providers = [];
    providerSet.forEach((providerId) => {
      if (providerId === "google.com") providers.push("google");
      else if (providerId === "facebook.com") providers.push("facebook");
      else if (providerId === "password") providers.push("password");
      else providers.push(providerId);
    });

    const email = (user.email || "").trim().toLowerCase();
    let hasPassword = providers.includes("password");
    if (email) {
      try {
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.includes("password")) {
          hasPassword = true;
          if (!providers.includes("password")) {
            providers.push("password");
          }
        }
      } catch (error) {
        console.warn("Không thể kiểm tra sign-in methods:", error);
      }
    }

    return { email, providers: [...new Set(providers)], hasPassword };
  };

  const handleEmailLoginSubmit = async (e) => {
    e.preventDefault();
    setEmailLoginError("");
    setEmailLoginInfo("");
    const normalizedEmail = emailLogin.email.trim().toLowerCase();
    if (!normalizedEmail || !emailLogin.password) {
      setEmailLoginError("Vui lòng nhập đầy đủ email và mật khẩu.");
      return;
    }

    setIsLoading(true);
    try {
      const auth = getAuth(app);
      const result = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        emailLogin.password
      );
      const user = result.user;
      const token = await user.getIdToken();
      const authMetadata = await getAuthMetadata(auth, user, ["password"]);

      localStorage.setItem("userId", user.uid);
      localStorage.setItem("userAccessToken", token);
      localStorage.setItem("userEmail", user.email || normalizedEmail);
      localStorage.setItem("loginMethod", "email_password");

      await checkAndSaveUser(user.uid, user.email || normalizedEmail, authMetadata);

      setIsLoggedIn(true);
      localStorage.setItem("redirecting", "true");
      redirectAfterLogin();
    } catch (error) {
      console.error("Lỗi đăng nhập Email/Password:", error);
      if (
        error.code === "auth/invalid-credential" ||
        error.code === "auth/wrong-password"
      ) {
        setEmailLoginError("Email hoặc mật khẩu chưa đúng.");
      } else if (error.code === "auth/user-not-found") {
        setEmailLoginError("Tài khoản chưa tồn tại. Hãy đăng nhập bằng Google trước.");
      } else if (error.code === "auth/too-many-requests") {
        setEmailLoginError("Bạn thử quá nhiều lần. Vui lòng đợi vài phút rồi thử lại.");
      } else {
        setEmailLoginError("Đăng nhập thất bại. Vui lòng thử lại.");
      }
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setEmailLoginError("");
    setEmailLoginInfo("");
    const normalizedEmail = emailLogin.email.trim().toLowerCase();
    if (!normalizedEmail) {
      setEmailLoginError("Vui lòng nhập email trước khi quên mật khẩu.");
      return;
    }

    setIsLoading(true);
    try {
      const auth = getAuth(app);
      await sendPasswordResetEmail(auth, normalizedEmail);
      setEmailLoginInfo("Đã gửi email đặt lại mật khẩu. Vui lòng kiểm tra hộp thư.");
    } catch (error) {
      console.error("Lỗi quên mật khẩu:", error);
      if (error.code === "auth/user-not-found") {
        setEmailLoginError("Email chưa tồn tại trong hệ thống.");
      } else if (error.code === "auth/invalid-email") {
        setEmailLoginError("Email không hợp lệ.");
      } else if (error.code === "auth/too-many-requests") {
        setEmailLoginError("Bạn thao tác quá nhiều lần. Vui lòng thử lại sau.");
      } else if (error.code === "auth/operation-not-allowed") {
        setEmailLoginError(
          "Hệ thống chưa bật tính năng Email/Password trong Firebase Authentication."
        );
      } else {
        setEmailLoginError("Không gửi được email đặt lại mật khẩu. Vui lòng thử lại.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookLogin = () => {
    // Track the click event with Meta Pixel
    trackEvent("FacebookLoginClick", { button_location: "login_page" });

    console.log("🔵 [FB Login] handleFacebookLogin called");
    console.log("🔵 [FB Login] window.FB exists:", !!window.FB);
    console.log("🔵 [FB Login] window.fbInitialized:", !!window.fbInitialized);

    setIsLoading(true);

    // Kiểm tra FB SDK đã khởi tạo đúng cách chưa (không chỉ tồn tại mà phải FB.init() xong)
    if (!window.FB || !window.fbInitialized) {
      console.warn("⚠️ [FB Login] FB SDK chưa init xong, dùng redirect flow");
      startRedirectLogin();
      return;
    }

    const fallbackController = createFacebookLoginFallback({
      timeoutMs: 3000,
      onFallback: () => {
        console.warn(
          "⚠️ [FB Login] FB.login() timeout trước khi popup được mở, fallback sang redirect"
        );
        startRedirectLogin();
      },
    });

    fallbackController.start();

    try {
      window.FB.login(
        (response) => {
          fallbackController.markCallbackHandled();
          console.log("🔵 [FB Login] Callback fired, status:", response.status);
          handleFBLoginResponse(response);
        },
        {
          scope: FACEBOOK_MANAGEMENT_SCOPE,
          auth_type: "rerequest",
        }
      );
    } catch (error) {
      fallbackController.cleanup();
      console.error("❌ [FB Login] FB.login() threw error:", error);
      startRedirectLogin();
    }
  };

  // Fallback: redirect trực tiếp đến Facebook OAuth dialog
  const startRedirectLogin = () => {
    const appId = process.env.REACT_APP_FB_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin + "/login");
    const scope = FACEBOOK_MANAGEMENT_SCOPE;
    const fbLoginUrl = `https://www.facebook.com/v21.0/dialog/oauth?client_id=${appId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=token&auth_type=rerequest`;

    console.log("🔵 [FB Login] Redirecting to:", fbLoginUrl);
    window.location.href = fbLoginUrl;
  };

  // Xử lý access_token nhận từ OAuth redirect flow
  const handleOAuthRedirectToken = async (accessToken, expiresIn) => {
    console.log("🔵 [FB Login] Processing OAuth redirect token");
    try {
      // Exchange short-lived token → long-lived token
      const res = await fetch(
        `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${process.env.REACT_APP_FB_CLIENT_ID}&client_secret=${process.env.REACT_APP_FB_CLIENT_SECRET}&fb_exchange_token=${accessToken}`
      );
      const data = await res.json();

      const longLivedToken = data.access_token || accessToken;
      const tokenExpiresIn = data.expires_in || expiresIn || 5184000; // Default 60 ngày
      const expiryTime = Date.now() + tokenExpiresIn * 1000;

      // Lấy user info từ token
      const meRes = await fetch(
        `https://graph.facebook.com/v21.0/me?access_token=${longLivedToken}`
      );
      const meData = await meRes.json();
      const userId = meData.id;

      if (!userId) {
        console.error("❌ Không lấy được userID từ token");
        alert("Đăng nhập Facebook không thành công! Không lấy được thông tin user.");
        setIsLoading(false);
        return;
      }

      // Lưu vào localStorage
      localStorage.setItem("userAccessToken", longLivedToken);
      localStorage.setItem("userAccessTokenExpiry", expiryTime);
      localStorage.setItem("loginMethod", "facebook");
      localStorage.setItem("userId", userId);
      localStorage.setItem("redirecting", "true");
      // KHÔNG gọi setIsLoggedIn(true) — window.location.href sẽ reload trang
      // Gọi ở đây gây race condition: App.js re-render → /login unmount → catch-all → /
      setUserID(userId);

      // Lưu vào Firebase
      const db = getDatabase();
      const userTokenRef = ref(db, `users/${userId}/facebookAuth`);
      await checkAndSaveUser(userId, null);
      const grantedScopes = await fetchGrantedFacebookScopes(longLivedToken);
      await update(userTokenRef, {
        userAccessToken: longLivedToken,
        userAccessTokenExpiry: expiryTime,
        tokenType: "long_lived_user_token",
        lastUpdated: Date.now(),
        grantedScopes,
      });

      // Lưu facebookAccessToken riêng (cho Google login users)
      localStorage.setItem("facebookAccessToken", longLivedToken);
      localStorage.setItem("facebookAccessTokenExpiry", expiryTime);

      // LUÔN fetch fanpages sau khi login thành công (cả login thường và connect fanpage)
      const pendingAction = localStorage.getItem("fb_pending_action");
      console.log("🔵 [FB Redirect] Fetching fanpages after login... (pendingAction:", pendingAction, ")");
      try {
        const fanpageRes = await fetch(
          `https://graph.facebook.com/v21.0/me/accounts?access_token=${longLivedToken}&fields=id,name,access_token,category&limit=100`
        );
        const fanpageData = await fanpageRes.json();

        if (fanpageData.data && fanpageData.data.length > 0) {
          const connectedFanpagesRef = ref(db, `users/${userId}/connectedFanpages`);
          const updatedFanpages = {};

          for (const page of fanpageData.data) {
            updatedFanpages[page.id] = buildConnectedFanpageRecord(page, {
              accessToken: page.access_token || null,
              grantedScopes,
              tokenStatus: page.access_token ? "active" : "error",
            });
          }

          await set(connectedFanpagesRef, updatedFanpages);
          await set(
            ref(db, `users/${userId}/connectedChannels/facebook`),
            buildFacebookConnectedChannelMap(updatedFanpages)
          );
          console.log(`✅ [FB Redirect] Saved ${fanpageData.data.length} fanpages to Firebase`);
        } else {
          console.warn("⚠️ [FB Redirect] No fanpages returned from API");
        }
      } catch (fanpageError) {
        console.error("❌ [FB Redirect] Error fetching fanpages:", fanpageError);
      }
      if (pendingAction) {
        localStorage.removeItem("fb_pending_action");
      }

      console.log("✅ OAuth redirect login thành công, chuyển hướng...");
      redirectAfterLogin();
    } catch (error) {
      console.error("❌ Lỗi xử lý OAuth redirect token:", error);
      // Vẫn redirect cho dù có lỗi — localStorage đã lưu token rồi
      redirectAfterLogin();
    }
  };

  // Xử lý response từ FB.login() callback
  const handleFBLoginResponse = (response) => {
    if (response.status === "connected") {
      const shortLivedToken = response.authResponse.accessToken;
      fetch(
        `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${process.env.REACT_APP_FB_CLIENT_ID}&client_secret=${process.env.REACT_APP_FB_CLIENT_SECRET}&fb_exchange_token=${shortLivedToken}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.access_token) {
            const accessToken = data.access_token;
            const expiryTime = Date.now() + data.expires_in * 1000;
            const userId = response.authResponse.userID;

            // Lưu vào localStorage
            localStorage.setItem("userAccessToken", accessToken);
            localStorage.setItem("userAccessTokenExpiry", expiryTime);
            localStorage.setItem("facebookAccessToken", accessToken);
            localStorage.setItem("facebookAccessTokenExpiry", expiryTime);
            localStorage.setItem("loginMethod", "facebook");
            localStorage.setItem("userId", userId);
            localStorage.setItem("redirecting", "true");
            // KHÔNG gọi setIsLoggedIn(true) ở đây — window.location.href sẽ reload trang
            // và App.js sẽ đọc localStorage để set login state. Gọi setIsLoggedIn() ở đây
            // gây race condition: App.js re-render ngay → /login không tồn tại → redirect về /
            setUserID(userId);

            // Lưu token và thời gian hết hạn vào realtime database
            const db = getDatabase();
            const userTokenRef = ref(db, `users/${userId}/facebookAuth`);

            const validExpiryTime = !isNaN(expiryTime)
              ? expiryTime
              : Date.now() + 60 * 24 * 60 * 60 * 1000;

            checkAndSaveUser(userId, null)
              .then(async () => {
                console.log("✅ Đã kiểm tra và lưu thông tin người dùng");
                const grantedScopes = await fetchGrantedFacebookScopes(
                  accessToken
                );
                return update(userTokenRef, {
                  userAccessToken: accessToken,
                  userAccessTokenExpiry: validExpiryTime,
                  tokenType: "long_lived_user_token",
                  lastUpdated: Date.now(),
                  grantedScopes,
                });
              })
              .then(async () => {
                console.log("✅ Đã lưu token vào Firebase Realtime DB");
                const grantedScopes = await fetchGrantedFacebookScopes(
                  accessToken
                );

                // Fetch fanpages và lưu vào Firebase
                try {
                  console.log("🔵 [FB Login] Fetching fanpages after login...");
                  const fanpageRes = await fetch(
                    `https://graph.facebook.com/v21.0/me/accounts?access_token=${accessToken}&fields=id,name,access_token,category&limit=100`
                  );
                  const fanpageData = await fanpageRes.json();

                  if (fanpageData.data && fanpageData.data.length > 0) {
                    const connectedFanpagesRef = ref(db, `users/${userId}/connectedFanpages`);
                    const updatedFanpages = {};

                    for (const page of fanpageData.data) {
                      updatedFanpages[page.id] = buildConnectedFanpageRecord(page, {
                        accessToken: page.access_token || null,
                        grantedScopes,
                        tokenStatus: page.access_token ? "active" : "error",
                      });
                    }

                    await set(connectedFanpagesRef, updatedFanpages);
                    await set(
                      ref(db, `users/${userId}/connectedChannels/facebook`),
                      buildFacebookConnectedChannelMap(updatedFanpages)
                    );
                    console.log(`✅ [FB Login] Saved ${fanpageData.data.length} fanpages to Firebase`);
                  } else {
                    console.warn("⚠️ [FB Login] No fanpages returned from API");
                  }
                } catch (fanpageError) {
                  console.error("❌ [FB Login] Error fetching fanpages:", fanpageError);
                }

                redirectAfterLogin();
              })
              .catch((tokenError) => {
                console.error("❌ Lỗi khi lưu token vào Firebase:", tokenError);
                // Vẫn redirect cho dù Firebase fail — localStorage đã lưu token rồi
                redirectAfterLogin();
              });
          } else {
            console.error("Failed to get long-lived access token:", data);
            alert("Đăng nhập Facebook không thành công!");
            setIsLoading(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching long-lived access token:", error);
          alert("Đăng nhập Facebook không thành công!");
          setIsLoading(false);
        });
    } else if (response.status === "not_authorized") {
      console.error("Facebook login not authorized:", response);
      alert(
        "Bạn đã từ chối cấp quyền cho ứng dụng. Vui lòng thử lại và cho phép các quyền cần thiết."
      );
      setIsLoading(false);
    } else {
      console.error("Facebook login failed:", response);
      setIsLoading(false);
    }
  };

  const checkAndSaveUser = async (userID, userEmail = "", authMetadata = null) => {
    try {
      // Get stored ad parameters using our utility function
      const adParams = getStoredAdParams() || {};

      // Get first-touch source attribution data
      const sourceData = getFirstTouch() || {};

      // Get login method for attribution
      const loginMethod = localStorage.getItem("loginMethod") || "unknown";

      // Đầu tiên, gọi API check-and-save
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/check-and-save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: userID,
            adParams: adParams, // Send ad parameters to backend
            sourceData: sourceData, // Send first-touch source attribution
            loginMethod: loginMethod, // Send login method (google/facebook/email_password)
            email: userEmail, // Gửi email lên API backend
            authMetadata,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        console.log("✅ Kiểm tra người dùng thành công:", data.data);

        // Sau khi API check-and-save thành công, lưu thông tin vào Firebase Realtime Database
        const db = getDatabase(app);
        const userRef = ref(db, `users/${userID}`);

        // Lấy thông tin user hiện có (nếu có)
        const userSnapshot = await get(userRef);

        // Cập nhật thông tin user với email
        const userData = {
          lastLogin: new Date().toISOString(),
        };

        // Chỉ cập nhật email nếu có giá trị
        if (userEmail) {
          userData.email = userEmail;
        }

        // Nếu là user mới, thêm thời gian đăng ký
        if (!userSnapshot.exists()) {
          userData.registeredAt = new Date().toISOString();
        }

        // Cập nhật thông tin vào database
        await update(userRef, userData);

        console.log("✅ Đã lưu thông tin người dùng vào Firebase:", userData);

        // Lưu thông tin token người dùng vào database nếu có
        const userAccessToken = localStorage.getItem("userAccessToken");
        const userAccessTokenExpiry = localStorage.getItem(
          "userAccessTokenExpiry"
        );

        if (userAccessToken && userAccessTokenExpiry) {
          const userTokenRef = ref(db, `users/${userID}/facebookAuth`);

          // Chuyển đổi userAccessTokenExpiry thành số và kiểm tra tính hợp lệ
          let tokenExpiry;
          try {
            tokenExpiry = parseInt(userAccessTokenExpiry);
            // Kiểm tra nếu là NaN sau khi parse
            if (isNaN(tokenExpiry)) {
              tokenExpiry = Date.now() + 60 * 24 * 60 * 60 * 1000; // Mặc định 60 ngày
              console.log(
                "⚠️ Token expiry không hợp lệ, sử dụng giá trị mặc định 60 ngày"
              );
            }
          } catch (error) {
            tokenExpiry = Date.now() + 60 * 24 * 60 * 60 * 1000; // Mặc định 60 ngày
            console.log(
              "⚠️ Lỗi khi parse token expiry, sử dụng giá trị mặc định 60 ngày:",
              error
            );
          }

          await update(userTokenRef, {
            userAccessToken: userAccessToken,
            userAccessTokenExpiry: tokenExpiry,
            tokenType: "long_lived_user_token",
            lastUpdated: Date.now(),
          });
          console.log(
            "✅ Đã cập nhật token người dùng vào Firebase trong checkAndSaveUser"
          );
        }

        // Fanpages đã được fetch bằng fetch() API ở handleFBLoginResponse và handleOAuthRedirectToken
        // Không dùng window.FB.api ở đây vì callback có thể không fire → treo vĩnh viễn
        console.log("ℹ️ Bỏ qua lấy fanpage trong checkAndSaveUser (đã xử lý ở caller)");

        // Thêm tracking affiliate
        try {
          const isNewUser = parseCheckAndSaveIsNewUser(data);
          if (isNewUser === true) {
            console.log("🔄 Bắt đầu ghi nhận thông tin affiliate cho user mới...");
            await trackAffiliateSignup(userID, true);
            console.log("✅ Hoàn thành ghi nhận thông tin affiliate");
            // Ghi cờ hiển thị popup trải nghiệm 1 ngày Premium dành cho người dùng mới
            localStorage.setItem("showNewUserTrialPopup", "true");
            console.log("🎁 Đã thiết lập cờ showNewUserTrialPopup cho tài khoản mới");
          } else if (isNewUser === false) {
            console.log(
              "ℹ️ Bỏ qua tracking affiliate vì đây không phải user mới"
            );
          } else {
            console.warn(
              "⚠️ Không xác định được trạng thái isNewUser từ API check-and-save, bỏ qua affiliate tracking để tránh attribution sai.",
              data
            );
          }

          // Track Facebook ad conversion if we have fbclid
          const adParams = getStoredAdParams() || {};

          if (adParams.fbclid) {
            console.log("📊 Tracking Facebook ad conversion for registration");
            // Track a custom event for Facebook ad conversion
            trackEvent("FacebookAdConversion", {
              content_name: "registration",
              user_id: userID,
              ...adParams,
            });
          }
        } catch (affiliateError) {
          console.error(
            "❌ Lỗi khi ghi nhận thông tin affiliate:",
            affiliateError
          );
          // Tiếp tục thực hiện chuyển hướng ngay cả khi có lỗi với affiliate
        }

        // Chuyển hướng đã được xử lý trong phần đăng nhập Facebook
      } else {
        console.error("❌ Lỗi:", data.message);
        alert("Không thể lưu thông tin người dùng. Vui lòng thử lại sau.");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("❌ Lỗi khi gọi API:", error);
      alert(
        "Đã xảy ra lỗi khi xử lý thông tin đăng nhập. Vui lòng thử lại sau."
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-gradient-overlay"></div>
        <div className="login-pattern"></div>
      </div>

      <div className="login-content">
        <div className="login-header">
          <div
            className="logo-container"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <img
              src={logo}
              alt="AUTOWORK"
              className="login-logo"
              style={{ width: "250px", height: "auto", maxWidth: "100%" }}
            />
          </div>
          <h2 className="brand-tagline">Tự động hóa công việc của bạn</h2>
        </div>

        <div className="login-box">
          <div className="login-welcome">
            <h1 className="login-title">Chào mừng trở lại</h1>
            <p className="login-subtitle">
              Kết nối tài khoản để tiếp tục sử dụng AutoWork
            </p>
          </div>

          <div className="login-buttons">
            <button
              className="login-button google-login"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <div className="button-content">
                <FcGoogle className="login-icon" />
                <span className="button-text">
                  {isLoading ? "Đang kết nối..." : "Tiếp tục với Google"}
                </span>
              </div>
              {/* {isLoading && <div className="loading-spinner"></div>} */}
            </button>

            <div className="divider">
              <span className="divider-text">hoặc</span>
            </div>

            <button
              className="login-button facebook-login"
              onClick={handleFacebookLogin}
              disabled={isLoading}
            >
              <div className="button-content">
                <FaFacebook className="login-icon" />
                <span className="button-text">
                  {isLoading ? "Đang kết nối..." : "Tiếp tục với Facebook"}
                </span>
              </div>
              {/* {isLoading && <div className="loading-spinner"></div>} */}
            </button>

            <div className="divider">
              <span className="divider-text">hoặc đăng nhập bằng email</span>
            </div>

            <form className="email-login-form" onSubmit={handleEmailLoginSubmit}>
              <input
                className="email-login-input"
                type="email"
                placeholder="Địa chỉ email"
                value={emailLogin.email}
                onChange={(e) =>
                  setEmailLogin((prev) => ({ ...prev, email: e.target.value }))
                }
                disabled={isLoading}
              />
              <input
                className="email-login-input"
                type="password"
                placeholder="Mật khẩu"
                value={emailLogin.password}
                onChange={(e) =>
                  setEmailLogin((prev) => ({ ...prev, password: e.target.value }))
                }
                disabled={isLoading}
              />
              {emailLoginError && (
                <p className="email-login-error">{emailLoginError}</p>
              )}
              {emailLoginInfo && (
                <p className="email-login-info">{emailLoginInfo}</p>
              )}
              <button
                type="button"
                className="email-forgot-link"
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                Quên mật khẩu?
              </button>
              <button
                className="login-button email-password-login"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Đang đăng nhập..." : "Đăng nhập với Email"}
              </button>
            </form>
          </div>

          <div className="login-footer">
            <div className="security-badge">
              <svg className="security-icon" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="m9 12 2 2 4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Kết nối an toàn & bảo mật</span>
            </div>

            <div className="terms">
              Bằng cách tiếp tục, bạn đồng ý với{" "}
              <a href="/terms" className="terms-link">
                Điều khoản dịch vụ
              </a>{" "}
              và{" "}
              <a href="/privacy" className="terms-link">
                Chính sách bảo mật
              </a>{" "}
              của chúng tôi
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginFB;
